import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { NavBar } from "../components/NavBar";
import { getPaymentStatus } from "../lib/api";
import { useEntitlements } from "../lib/entitlements";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh, status, trial_end } = useEntitlements();
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const polls = useRef(0);
  const trialDate = trial_end ? new Date(trial_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.payment_status === "paid") {
          setState("paid");
          refresh(); // refresh entitlements in the background — don't block the success UI
          return;
        }
        if (s.status === "expired" || s.payment_status === "expired") {
          setState("error");
          return;
        }
      } catch {
        // keep trying
      }
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <>
      <NavBar />
      <main className="container-nc" style={{ paddingTop: 70, paddingBottom: 90, maxWidth: 520, textAlign: "center" }}>
        {state === "polling" && (
          <>
            <Loader2 size={44} className="spin" color="var(--brand)" style={{ margin: "0 auto" }} />
            <h1 style={{ fontSize: "1.8rem", fontWeight: 600, marginTop: 20 }}>Confirming your payment…</h1>
            <p style={{ color: "var(--muted)" }}>This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <div className="fade-up" data-testid="payment-success">
            <span style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(34,197,94,0.14)", display: "grid", placeItems: "center", margin: "0 auto" }}>
              <CheckCircle2 size={38} color="var(--success)" />
            </span>
            <h1 style={{ fontSize: "2rem", fontWeight: 600, marginTop: 20 }}>
              {status === "trialing" ? "Your free trial is live! 🎉" : "You're all set! 🎉"}
            </h1>
            <p style={{ color: "var(--muted)", lineHeight: 1.6 }}>
              {status === "trialing"
                ? `Your 3-day free trial has started${trialDate ? ` — you won't be charged until ${trialDate}` : ""}. Enjoy unlimited analyses, synced across the web and the iOS app. Cancel anytime from Settings before the trial ends.`
                : "Unlimited analyses are unlocked and synced to your account across the web and the iOS app."}
            </p>
            <button className="btn btn-primary btn-lg" style={{ marginTop: 22 }} onClick={() => navigate("/app")} data-testid="success-continue">
              Start analysing
            </button>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <span style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center", margin: "0 auto" }}>
              <XCircle size={38} color="var(--brand)" />
            </span>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 600, marginTop: 20 }}>
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p style={{ color: "var(--muted)", lineHeight: 1.6 }}>
              {state === "timeout"
                ? "Your payment is taking longer than usual. If you completed checkout, your Pro access will appear shortly."
                : "We couldn't confirm this payment. If you were charged, it will activate automatically."}
            </p>
            <button className="btn btn-ghost btn-lg" style={{ marginTop: 22 }} onClick={() => navigate("/")}>
              Back to home
            </button>
          </>
        )}
      </main>
    </>
  );
}
